// src/pages/blog/BlogPost.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import matter from 'gray-matter';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUser, faArrowLeft, faClock, faShare } from '@fortawesome/free-solid-svg-icons';

const BlogPost = () => {
    const { slug } = useParams();
    const [content, setContent] = useState('');
    const [meta, setMeta] = useState({
        title: 'Blog Post',
        description: 'Descripción del post',
        date: new Date().toISOString(),
        author: 'Equipo PetitPrime',
        readTime: '5 min'
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        import(`../../posts/${slug}.md`)
            .then((module) => fetch(module.default))
            .then((res) => res.text())
            .then((text) => {
                // Usa gray-matter para extraer frontmatter y contenido
                const { data, content: mdContent } = matter(text);
                setContent(mdContent);
                setMeta({
                    title: data.title || 'Blog Post',
                    description: data.description || 'Descripción del post',
                    date: data.date || new Date().toISOString(),
                    author: data.author || 'Equipo PetitPrime',
                    readTime: data.readTime || '5 min'
                });
            })
            .catch((err) => {
                console.error('Error al cargar el post:', err);
                setContent('# Post no encontrado\n\nLo sentimos, este artículo no está disponible en este momento.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [slug]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando artículo...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>{meta.title} | PetitPrime Blog</title>
                <meta name="description" content={meta.description || meta.title} />
                <meta name="keywords" content="fidelización, clientes, pymes, software, blog" />
            </Helmet>

            {/* Hero Section */}
            <header className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-16">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <Link
                            to="/blog"
                            className="inline-flex items-center text-blue-200 hover:text-white mb-6 transition-colors duration-300"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                            Volver al Blog
                        </Link>

                        <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                            {meta.title}
                        </h1>

                        <div className="flex flex-wrap items-center text-blue-200 text-sm mb-6">
                            <div className="flex items-center mr-6 mb-2">
                                <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                                <span>{formatDate(meta.date)}</span>
                            </div>
                            <div className="flex items-center mr-6 mb-2">
                                <FontAwesomeIcon icon={faUser} className="mr-2" />
                                <span>{meta.author}</span>
                            </div>
                            <div className="flex items-center mb-2">
                                <FontAwesomeIcon icon={faClock} className="mr-2" />
                                <span>{meta.readTime}</span>
                            </div>
                        </div>

                        <p className="text-xl text-blue-100 leading-relaxed">
                            {meta.description}
                        </p>
                    </div>
                </div>
            </header>

            {/* Article Content */}
            <main className="py-16">
                <div className="container mx-auto px-4">
                    <article className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
                        <div className="p-8 lg:p-12">
                            <div className="prose prose-lg max-w-full text-gray-800 prose-headings:text-gray-900 prose-a:text-blue-600 prose-strong:text-gray-900 prose-blockquote:border-l-blue-600 prose-blockquote:bg-blue-50 prose-blockquote:py-2 prose-blockquote:px-4">
                                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                                    {content}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </article>

                    {/* Share and Navigation */}
                    <div className="max-w-4xl mx-auto mt-8">
                        <div className="bg-white rounded-2xl shadow-lg p-6">
                            <div className="flex flex-col sm:flex-row justify-between items-center">
                                <div className="flex items-center mb-4 sm:mb-0">
                                    <FontAwesomeIcon icon={faShare} className="mr-2 text-gray-600" />
                                    <span className="text-gray-700 font-medium">Compartir artículo:</span>
                                </div>
                                <div className="flex space-x-4">
                                    <button
                                        onClick={() => navigator.share?.({ title: meta.title, url: window.location.href })}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300"
                                    >
                                        Compartir
                                    </button>
                                    <Link
                                        to="/blog"
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300"
                                    >
                                        Ver más artículos
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold mb-6">
                        ¿Te Gustó este Artículo?
                    </h2>
                    <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
                        Pon en práctica estas estrategias con PetitPrime y transforma
                        la fidelización de tus clientes
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/register"
                            className="bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-bold py-4 px-8 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
                        >
                            Comenzar Prueba Gratis
                        </Link>
                        <Link
                            to="/blog"
                            className="border-2 border-white hover:bg-white hover:text-blue-600 text-white font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 flex items-center justify-center"
                        >
                            Ver más artículos
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BlogPost;
